import React, { useState, useEffect } from 'react';
import { FiSearch, FiEye } from 'react-icons/fi';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Input from '../components/Input';
import { apiClient } from '../utils/api';

const StudentsList = ({ user, onLogout }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Fetch students data
  const fetchStudents = async (page = 1, searchTerm = '') => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        limit,
        ...(searchTerm && { search: searchTerm })
      };

      const response = await apiClient.get('students', params);
      const { data } = response;
      
      setStudents(data.data || []);
      setTotal(data.total || 0);
      setTotalPages(Math.ceil((data.total || 0) / limit));
      setCurrentPage(page);
    } catch (err) {
      setError(err.message || 'Failed to fetch students');
      console.error('Error fetching students:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch student details
  const fetchStudentDetails = async (id) => {
    try {
      setModalLoading(true);
      const response = await apiClient.get(`students/${id}`);
      setSelectedStudent(response.data);
    } catch (err) {
      console.error('Error fetching student details:', err);
      setError(err.message || 'Failed to fetch student details');
    } finally {
      setModalLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchStudents(1, search);
  };

  // Handle page change
  const handlePageChange = (page) => {
    fetchStudents(page, search);
  };

  // Handle view student details
  const handleViewStudent = async (id) => {
    await fetchStudentDetails(id);
    setShowModal(true);
  };

  const handleColumnFilter = (key, value) => {
    // This function is required by DataTable but not used in this implementation
    // since we're handling search at the API level
  };

  const handleSort = (sortConfig) => {
    // This function is required by DataTable but not used in this implementation
    // since we're handling sorting at the API level
  };

  const handleRowAction = (actionKey, row, index) => {
    // This function is required by DataTable but not used in this implementation
    // since we're handling actions directly in the render function
  };

  const handleRowSelect = (selectedRows) => {
    // This function is required by DataTable but not used in this implementation
    // since we're not using row selection in this table
  };

  const handlePageSizeChange = (newPageSize) => {
    // This function is required by DataTable but not used in this implementation
    // since we're using a fixed page size
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
  };

  // Initial data fetch
  useEffect(() => {
    fetchStudents(1, '');
  }, []);

  // Table columns configuration
  const columns = [
    {
      key: 'user',
      title: 'Student',
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <img
            src={row.user?.image}
            alt={row.user?.name}
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <div className="font-medium text-gray-900">{row.user?.name}</div>
            <div className="text-sm text-gray-500">{row.user?.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'major',
      title: 'Major',
      render: (value, row) => (
        <div className="font-medium text-gray-900">{row.major}</div>
      ),
    },
    {
      key: 'university',
      title: 'University',
      render: (value, row) => (
        <div className="text-gray-600">{row.university}</div>
      ),
    },
    {
      key: 'graduation_year',
      title: 'Graduation Year',
      render: (value, row) => (
        <div className="text-gray-600">{row.graduation_year}</div>
      ),
    },
    {
      key: 'gpa',
      title: 'GPA',
      render: (value, row) => (
        <div className="text-gray-600">{row.gpa}</div>
      ),
    },
    {
      key: 'phone',
      title: 'Phone',
      render: (value, row) => (
        <div className="text-gray-600">{row.country_code} {row.phone}</div>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (value, row) => (
        <button
          onClick={() => handleViewStudent(row.id)}
          className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-50 transition-colors"
          title="View Details"
        >
          <FiEye className="h-5 w-5" />
        </button>
      ),
    },
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="">
        <PageHeader
            title="Students List"
          description="Manage and view student information"
          user={user}
        />

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSearch} className="flex space-x-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search students by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full"
              />
            </div>
            <button
              type="submit"
              className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-2 rounded-md transition duration-200 flex items-center space-x-2"
            >
              <FiSearch className="h-4 w-4" />
              <span>Search</span>
            </button>
          </form>
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Students List</h2>
            
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <span className="ml-2 text-gray-600">Loading students...</span>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-red-800">{error}</div>
              </div>
            ) : (
              <>
                <DataTable
                  data={students}
                  columns={columns}
                  loading={loading}
                  emptyMessage="No students found"
                  pagination={{
                    current: currentPage,
                    total: total,
                    pageSize: limit,
                    totalPages: totalPages
                  }}
                  onSort={handleSort}
                  onFilter={handleColumnFilter}
                  onRowAction={handleRowAction}
                  onRowSelect={handleRowSelect}
                  onPageSizeChange={handlePageSizeChange}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </div>
        </div>

        {/* Student Details Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Student Details</h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {modalLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                    <span className="ml-2 text-gray-600">Loading...</span>
                  </div>
                ) : selectedStudent ? (
                  <div className="space-y-6">
                    {/* Student Header */}
                    <div className="flex items-center space-x-4">
                      <img
                        src={selectedStudent.user?.image }
                        alt={selectedStudent.user?.name}
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900">{selectedStudent.user?.name}</h4>
                        <p className="text-gray-600">{selectedStudent.user?.email}</p>
                      </div>
                    </div>

                    {/* Academic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">Major</h5>
                        <p className="text-gray-600">{selectedStudent.major}</p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">University</h5>
                        <p className="text-gray-600">{selectedStudent.university}</p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">Graduation Year</h5>
                        <p className="text-gray-600">{selectedStudent.graduation_year}</p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">GPA</h5>
                        <p className="text-gray-600">{selectedStudent.gpa}</p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">Level</h5>
                        <p className="text-gray-600">{selectedStudent.level}</p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">Nationality</h5>
                        <p className="text-gray-600">{selectedStudent.nationality?.nationality}</p>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div>
                      <h5 className="font-medium text-gray-900 mb-3">Contact Information</h5>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-600">Phone:</span>
                          <span className="font-medium">{selectedStudent.country_code} {selectedStudent.phone}</span>
                        </div>
                        {selectedStudent.national_id && (
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">National ID:</span>
                            <span className="font-medium">{selectedStudent.national_id}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No student details available
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200">
                <button
                  onClick={closeModal}
                  className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudentsList;
